import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── In-memory access token (never in localStorage) ────────────────────────────
let _accessToken = null;
export const setAccessToken   = (t) => { _accessToken = t; };
export const clearAccessToken = () => { _accessToken = null; };

const api = axios.create({
  baseURL:      API_URL,
  withCredentials: true,
  headers:      { 'Content-Type': 'application/json' },
  timeout:      30000
});

api.interceptors.request.use((config) => {
  if (_accessToken) config.headers.Authorization = `Bearer ${_accessToken}`;
  return config;
}, (err) => Promise.reject(err));

// Auto-refresh on TOKEN_EXPIRED
let _refreshPromise = null;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const code     = error.response?.data?.code;
    const status   = error.response?.status;

    if (status === 401 && code === 'TOKEN_EXPIRED' && !original._retry) {
      original._retry = true;
      if (!_refreshPromise) {
        _refreshPromise = axios
          .post(`${API_URL}/auth/refresh`, {}, { withCredentials: true })
          .then(({ data }) => { setAccessToken(data.accessToken); return data.accessToken; })
          .catch((err) => {
            clearAccessToken();
            const publicPaths = ['/', '/browse', '/login', '/register', '/verify-email', '/forgot-password', '/reset-password'];
            if (!publicPaths.some(p => window.location.pathname.startsWith(p))) window.location.href = '/login';
            return Promise.reject(err);
          })
          .finally(() => { _refreshPromise = null; });
      }
      try {
        const newToken = await _refreshPromise;
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch { return Promise.reject(error); }
    }
    return Promise.reject(error);
  }
);

// ─── AUTH API ─────────────────────────────────────────────────────────────────
export const authAPI = {
  register:           (data)         => api.post('/auth/register', data),
  verifyEmail:        (token)        => api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`),
  resendVerification: (data)         => api.post('/auth/resend-verification', data),
  login:              (data)         => api.post('/auth/login', data),
  forgotPassword:     (data)         => api.post('/auth/forgot-password', data),
  resetPassword:      (token, data)  => api.post(`/auth/reset-password?token=${encodeURIComponent(token)}`, data),
  refresh:            ()             => api.post('/auth/refresh'),
  getMe:              ()             => api.get('/auth/me'),
  updateProfile:      (data)         => api.put('/auth/profile', data),
  changePassword:     (data)         => api.put('/auth/change-password', data),
  sendPhoneOtp:       ()             => api.post('/auth/send-phone-otp'),
  verifyPhone:        (data)         => api.post('/auth/verify-phone', data),
  logout:             ()             => api.post('/auth/logout'),
  googleAuth:         (idToken)      => api.post('/auth/google', { idToken }),
};

// ─── USER API (profile section) ───────────────────────────────────────────────
export const userAPI = {
  getProfile:     ()     => api.get('/user/profile'),
  updateProfile:  (data) => api.put('/user/profile', data),
  changePassword: (data) => api.put('/user/change-password', data),
  getReferral:    ()     => api.get('/user/referral'),
};

// ─── PRODUCTS API ─────────────────────────────────────────────────────────────
export const productAPI = {
  getAll:            (params)     => api.get('/products', { params }),
  getOne:            (id)         => api.get(`/products/${id}`),
  getSuggestions:    (q)          => api.get('/products/suggest', { params: { q } }),
  checkAvailability: (id, params) => api.get(`/products/${id}/availability`, { params }),
};

// ─── CART API ─────────────────────────────────────────────────────────────────
export const cartAPI = {
  get:    ()             => api.get('/cart'),
  add:    (data)         => api.post('/cart', data),
  update: (itemId, data) => api.put(`/cart/${itemId}`, data),
  remove: (itemId)       => api.delete(`/cart/${itemId}`),
  clear:  ()             => api.delete('/cart'),
};

// ─── WISHLIST API ─────────────────────────────────────────────────────────────
export const wishlistAPI = {
  get:    ()   => api.get('/wishlist'),
  toggle: (id) => api.post(`/wishlist/${id}`),
  remove: (id) => api.delete(`/wishlist/${id}`),
  check:  (id) => api.get(`/wishlist/check/${id}`),
};

// ─── ORDERS API ───────────────────────────────────────────────────────────────
export const orderAPI = {
  create:           (data)     => api.post('/orders', data),
  getMy:            (params)   => api.get('/orders/my', { params }),
  getOne:           (id)       => api.get(`/orders/${id}`),
  cancel:           (id, data) => api.patch(`/orders/${id}/cancel`, data),
  requestReturn:    (id, data) => api.patch(`/orders/${id}/request-return`, data),
  requestExtension: (id, data) => api.patch(`/orders/${id}/request-extension`, data),
  buyAfterRental:   (id, data) => api.post(`/orders/${id}/buy`, data),
};

// ─── PAYMENT API ──────────────────────────────────────────────────────────────
export const paymentAPI = {
  // Stripe
  createCheckoutSession: (data) => api.post('/payment/create-checkout-session', data),
  verifySession:         (sessionId) => api.get(`/payment/verify-session?session_id=${sessionId}`),
  // COD
  placeCodOrder:  (data) => api.post('/payment/cod', data),
  getCodSettings: ()     => api.get('/payment/cod-settings'),
  // FCM tokens
  registerFcmToken: (data) => api.post('/payment/fcm-token', data),
  removeFcmToken:   (data) => api.delete('/payment/fcm-token', { data }),
};

// ─── IDENTITY API ─────────────────────────────────────────────────────────────
export const identityAPI = {
  submit:    (formData) => api.post('/identity/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getStatus: ()         => api.get('/identity/status'),
  getAll:    (params)   => api.get('/admin/identity', { params }),
  getOne:    (id)       => api.get(`/admin/identity/${id}`),
  review:    (id, data) => api.patch(`/admin/identity/${id}/review`, data),
};

// ─── DEPOSIT API ──────────────────────────────────────────────────────────────
export const depositAPI = {
  getAll:        (params)   => api.get('/admin/deposits', { params }),
  processRefund: (id, data) => api.patch(`/admin/deposits/${id}/refund`, data),
  markDeposit:   (id, data) => api.patch(`/admin/deposits/${id}/mark`, data),
};

// ─── ADMIN API ────────────────────────────────────────────────────────────────
export const adminAPI = {
  getStats:                ()          => api.get('/admin/stats'),
  createProduct:           (data)      => api.post('/admin/products', data),
  updateProduct:           (id, data)  => api.put(`/admin/products/${id}`, data),
  deleteProduct:           (id)        => api.delete(`/admin/products/${id}`),
  toggleProduct:           (id)        => api.patch(`/admin/products/${id}/toggle`),
  getAllOrders:             (params)    => api.get('/admin/orders', { params }),
  updateOrderStatus:       (id, data)  => api.patch(`/admin/orders/${id}/status`, data),
  markCodCollected:        (id)        => api.patch(`/admin/orders/${id}/cod-collected`),
  markCodDepositCollected: (id, data)  => api.patch(`/admin/orders/${id}/cod-deposit-collected`, data),
  handleExtension:         (id, data)  => api.patch(`/admin/orders/${id}/extension`, data),
  getUsers:                (params)    => api.get('/admin/users', { params }),
  getUserDetail:           (id)        => api.get(`/admin/users/${id}`),
  toggleUser:              (id)        => api.patch(`/admin/users/${id}/toggle`),
  exportOrders:            (params)    => api.get('/admin/orders/export', { params, responseType: 'blob' }),
};

// ─── REVIEW API ───────────────────────────────────────────────────────────────
export const reviewAPI = {
  getReviews:  (productId, params) => api.get(`/products/${productId}/reviews`, { params }),
  canReview:   (productId, params) => api.get(`/products/${productId}/reviews/can-review`, { params }),
  submit:      (productId, data)   => api.post(`/products/${productId}/reviews`, data),
};

// ─── ADDRESS API ──────────────────────────────────────────────────────────────
export const addressAPI = {
  getAll:  ()          => api.get('/user/addresses'),
  create:  (data)      => api.post('/user/addresses', data),
  update:  (id, data)  => api.put(`/user/addresses/${id}`, data),
  delete:  (id)        => api.delete(`/user/addresses/${id}`),
  setDefault: (id)     => api.patch(`/user/addresses/${id}/default`),
};

// ─── TRUST / WARBA SCORE API ──────────────────────────────────────────────────
export const trustAPI = {
  getMyScore:    ()       => api.get('/trust/me'),
  getMyTrust:    ()       => api.get('/trust/me'),          // alias
  getAdminView:  (userId) => api.get(`/trust/admin/${userId}`),
  banUser:       (data)   => api.post('/trust/ban', data),
  unbanUser:     (data)   => api.post('/trust/unban', data),
  verifyCollege: (data)   => api.post('/trust/verify-college', data),
  confirmCollege:(data)   => api.post('/trust/confirm-college', data),
  redeem:        (data)   => api.post('/trust/redeem', data),
};

// ─── DELIVERY API ─────────────────────────────────────────────────────────────
export const deliveryAPI = {
  getZones:  ()          => api.get('/delivery'),
  create:    (data)      => api.post('/delivery', data),
  update:    (id, data)  => api.put(`/delivery/${id}`, data),
  delete:    (id)        => api.delete(`/delivery/${id}`),
  getCharge: (params)    => api.get('/delivery/charge', { params }),
};

// ─── COUPON API ───────────────────────────────────────────────────────────────
export const couponAPI = {
  getAll:  (params)    => api.get('/coupon', { params }),
  create:  (data)      => api.post('/coupon', data),
  update:  (id, data)  => api.put(`/coupon/${id}`, data),
  delete:  (id)        => api.delete(`/coupon/${id}`),
  apply:   (data)      => api.post('/coupon/validate', data),
};

export default api;